import { API_URL } from "@/lib/api";
import { isDexieStorageError, StorageUnavailableError } from "@/lib/storageCapability";

export function isStorageFailure(error: unknown): boolean {
  if (error instanceof StorageUnavailableError) return true;
  if (isDexieStorageError(error)) return true;
  const msg = error instanceof Error ? error.message : String(error ?? "");
  return (
    /quota|storage|space|UnknownError|connection.*lost|indexed|null is not an object/i.test(msg) ||
    (error as any)?.name === "QuotaExceededError" ||
    (error as any)?.name === "UnknownError"
  );
}

export function errorMessage(error: unknown) {
  if (error instanceof Error) {
    const msg = error.message || error.name || "";
    if (error instanceof StorageUnavailableError || /storage is unavailable|private browsing/i.test(msg)) {
      return msg;
    }
    if (/quota|storage|space/i.test(msg) || (error as any).name === "QuotaExceededError") {
      return "Storage quota exceeded. " + msg;
    }
    if (/connection.*lost|indexed database server lost|UnknownError/i.test(msg) || (error as any).name === "UnknownError") {
      return "IndexedDB connection lost (browser storage issue). " + msg;
    }
    return msg;
  }
  return "Something went wrong.";
}

export type ReachabilityResult = { reachable: true } | { reachable: false; reason: string };

export function unreachable(reason: string): ReachabilityResult {
  return { reachable: false, reason };
}

export async function checkBackendReachable(timeoutMs = 2500): Promise<ReachabilityResult> {
  let timeout: ReturnType<typeof globalThis.setTimeout> | null = null;
  try {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      return unreachable("the browser is offline");
    }
    if (typeof fetch !== "function") {
      return unreachable("network requests are unavailable in this browser");
    }

    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    timeout = controller
      ? globalThis.setTimeout(() => controller.abort(), timeoutMs)
      : null;
    const response = await fetch(`${API_URL}/health?reachability=${Date.now()}`, {
      cache: "no-store",
      credentials: "include",
      headers: { Accept: "application/json" },
      method: "GET",
      signal: controller?.signal,
    });
    if (timeout !== null) globalThis.clearTimeout(timeout);
    if (response.ok) return { reachable: true };
    return unreachable(`the server responded with ${response.status}`);
  } catch (error) {
    const reason = (error as { name?: string } | null)?.name === "AbortError"
      ? "the server reachability check timed out"
      : "the server could not be reached";
    return unreachable(reason);
  } finally {
    if (timeout !== null) globalThis.clearTimeout(timeout);
  }
}

export function localSaveFailureMessage(itemName: "punch" | "field note", storageMessage: string, reachability: ReachabilityResult): string {
  const browserOnlineButUnreachable =
    typeof navigator !== "undefined" && navigator.onLine && !reachability.reachable;
  const simulatedOfflineHint = browserOnlineButUnreachable
    ? " The browser still reports online, but the server is unreachable; this can happen with simulated offline mode."
    : "";
  const reason = reachability.reachable ? "the server reachability check did not complete" : reachability.reason;
  return `Could not save the ${itemName} on this device (${storageMessage}). Direct server submit was skipped because ${reason}.${simulatedOfflineHint} The ${itemName} was not saved; restore network/local storage and try again.`;
}
